// LRPO-5849 — the Leads tab is rendered once at page load, so a lead scanned
// while the page is open never appears in it. The tab BADGE meanwhile updates on
// every autosave response (LRPO-5818), so the two visibly disagree — QA filmed
// "Leads 2" beside "Showing 1 to 1 of 1 entry".
//
// A scan already broadcasts { refresh: true }; this re-reads the leads_tab
// endpoint and swaps the rendered rows in. Markup and count come from the SAME
// response so they cannot be applied out of step.
//
// ⚠ innerHTML IS DELIBERATE, as it is in territory_sentence_controller: the
// payload is a server-RENDERED PARTIAL, not a server value, and every
// interpolation inside it goes through ERB's <%= %>. Do not "fix" it to
// textContent — that would render the table as visible markup.
import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = { exhibitorId: String, url: String }

  connect () {
    if (!this.exhibitorIdValue || !this.urlValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: "RepAssignmentLogsChannel", exhibitor_id: this.exhibitorIdValue },
      {
        received: (data) => {
          if (data && data.refresh) this.refresh()
        }
      }
    )
  }

  disconnect () {
    if (this.subscription) this.subscription.unsubscribe()
    this.subscription = null
  }

  async refresh () {
    let payload
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) return
      payload = await response.json()
    } catch (error) {
      return
    }
    if (!payload) return

    // A stale table beats an empty one, so only replace when we actually got markup.
    if (typeof payload.html === "string" && payload.html.length) this.replaceRows(payload.html)
    if (payload.assigned_leads_count != null) this.updateBadge(payload.assigned_leads_count)
    if (payload.criterion_counts) this.updateCriterionCounts(payload.criterion_counts)
  }

  replaceRows (html) {
    // The old DataTable keeps a handle on rows we are about to detach, and
    // re-initialising over them throws "cannot reinitialise". Destroy first.
    const existing = this.element.querySelector("#my-reps-leads table")
    if (existing && window.$ && window.$.fn && window.$.fn.DataTable &&
        window.$.fn.DataTable.isDataTable(existing)) {
      window.$(existing).DataTable().destroy()
    }

    this.element.innerHTML = html

    const fresh = this.element.querySelector("#my-reps-leads table")
    if (fresh && window.$ && window.DataTableConfig) {
      window.$(fresh).DataTable(window.DataTableConfig.getStandardConfig({ order: [[1, "desc"]] }))
    }
  }

  updateBadge (count) {
    const badge = document.getElementById("assigned-leads-count")
    if (badge) badge.textContent = String(count)
  }

  // LRPO-5875 — the company / demographic counts are rendered once server-side
  // and go stale the moment a lead is scanned. They are NOT inside this
  // controller's element: they live in the criteria cards elsewhere on the page,
  // so this searches the whole document by criterion id.
  //
  // The wording ("at this company" / "with this answer") stays in the ERB and
  // arrives on data-count-suffix, so this method carries no product copy.
  updateCriterionCounts (counts) {
    Object.keys(counts).forEach(id => {
      const value = Number(counts[id])
      // ids are database integers. Anything else is not ours to interpolate
      // into a selector, and CSS.escape does not exist in jsdom, so the shape
      // check is the guard instead of an escape.
      if (!Number.isFinite(value) || !/^\d+$/.test(String(id))) return

      // ⚠ [data-count-suffix] IS PART OF THE SELECTOR, not a convenience.
      // reconcileIds in territory_companies_controller also writes
      // data-criterion-id onto the HIDDEN FIELDS WRAPPER, which holds the
      // [id]/[kind]/[value] inputs for that criterion. Matching it and setting
      // textContent would delete those inputs and the next autosave would drop
      // the criterion. Only a count display declares a suffix.
      document.querySelectorAll(`[data-criterion-id="${id}"][data-count-suffix]`).forEach(el => {
        const suffix = el.dataset.countSuffix || ""
        el.textContent = suffix ? `${value} ${suffix}` : String(value)

        // Only the company chips have a zero state; demographic rows have no
        // such class, and closest() returns null there.
        const chip = el.closest(".territory-chip")
        if (chip) chip.classList.toggle("territory-chip--zero", value === 0)
      })
    })
  }
}
